import { Button, Switch, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useState } from 'react';

import { EAllowStatus, reverseEAllowStatus } from '~/src/dash/api/console-api';
import {
  INetworkInVideoUnit,
  INetworkTracker,
  NetworkInVideoUnitPartners,
  NetworkTrackerEventTypes,
} from '~/src/dash/api/network';
import { EBool } from '~/src/dash/api/types/bool';
import { IAccordionPanel, MultiAccordion } from '~/src/dash/components/accordion/multi-accordion';
import { IInputRow } from '~/src/dash/components/input-row';
import { NetworkBetaFeatures } from '~/src/dash/components/networks/network-beta-features';
import { INetworkBetaFeaturesProps } from '~/src/dash/components/networks/network-beta-features';
import { Text } from '~/src/dash/components/text';
import { ITrackerEvent, TrackerEvents } from '~/src/dash/components/tracker-events';
import { IWizardStepProps } from '~/src/dash/components/wizard';
import WizardButtons from '~/src/dash/components/wizard-buttons';
import { Col, Cols, Panel, Row, Rows } from '~/src/dash/publicaUI';

/**
 * All these props are state variables passed from
 * the parent component (network-settings-view)
 */
interface IOptionalNetworkSettingsFormProps extends IWizardStepProps {
  canWrite: boolean;
  Username: string;
  Timezone: string;
  publisherID: string;
  handleSave?: () => void;
  saveAttempted: boolean;

  // Trackers
  NetworkTrackers: INetworkTracker[];
  handleTrackerEventActiveSwitchChange: (index: number) => void;
  handleTrackerEventTypeChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => void;
  handleTrackerEventPixelURLChange: (text: string, index: number) => void;
  handleDeleteModelWithPromise: (trackerID: string) => Promise<unknown>;
  handleDeleteSuccess: (index: number) => void;
  handleDeleteError: () => void;
  addTracker: () => void;

  // In Video Units
  addInVideoUnit: () => void;
  NetworkInVideoUnits: INetworkInVideoUnit[];
  handleInVideoUnitActiveSwitchChange: (index: number) => void;
  handleInVideoUnitPartnerChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => void;
  handleInVideoUnitURLChange: (text: string, index: number) => void;
  handleInVideoUnitDeleteModelWithPromise: (unitID: string) => Promise<unknown>;
  handleInVideoUnitDeleteSuccess: (index: number) => void;
  handleInVideoUnitDeleteError: () => void;

  // Slate
  SlateURL: string;
  uploadingSlateMedia: boolean;
  updateSlateURL: (slateURL: string) => void;
  uploadMediafile: ({ target }: React.ChangeEvent<HTMLInputElement>) => void;

  // Targeting
  TargetingSCTECueIDs: string;
  TargetingSCTECueIDsIsAllowed: EAllowStatus;
  handleTargetingChange: ({ target }: React.ChangeEvent<HTMLInputElement>) => void;

  // Content Value Normalization
  NormalizeContentValues: EBool;
  handleToggleNormalizeContentValues: () => void;

  // Allow Silent Ads
  AllowSilentAds: EBool;
  handleToggleAllowSilentAds: () => void;

  // Beta Features
  BetaFeaturesAndHandlers?: INetworkBetaFeaturesProps;
}

const OptionalNetworkSettingsForm = ({
  onBack,
  Username,
  Timezone,
  canWrite,
  addTracker,
  handleSave,
  publisherID,
  saveAttempted,
  uploadingSlateMedia,

  // Trackers
  NetworkTrackers,
  handleDeleteError,
  handleDeleteSuccess,
  handleTrackerEventTypeChange,
  handleDeleteModelWithPromise,
  handleTrackerEventPixelURLChange,
  handleTrackerEventActiveSwitchChange,

  // In Video Units
  addInVideoUnit,
  NetworkInVideoUnits,
  handleInVideoUnitURLChange,
  handleInVideoUnitDeleteError,
  handleInVideoUnitPartnerChange,
  handleInVideoUnitDeleteSuccess,
  handleInVideoUnitActiveSwitchChange,
  handleInVideoUnitDeleteModelWithPromise,

  // Slate
  SlateURL,
  updateSlateURL,
  uploadMediafile,

  // Targeting
  TargetingSCTECueIDs,
  handleTargetingChange,
  TargetingSCTECueIDsIsAllowed,

  // Content Value Normalization
  NormalizeContentValues,
  handleToggleNormalizeContentValues,

  // Allow Silent Ads
  AllowSilentAds,
  handleToggleAllowSilentAds,

  // Beta Features
  BetaFeaturesAndHandlers,
}: IOptionalNetworkSettingsFormProps) => {
  return (
    <>
      <Typography
        style={{
          paddingLeft: '5px',
          marginTop: '0px',
          marginBottom: '1px',
          fontSize: '16px',
          fontWeight: 600,
        }}
      >
        Ready to get granular? Set extra options for this network
      </Typography>
      <Typography
        style={{
          fontSize: '14px',
          paddingLeft: '5px',
          marginTop: '0px',
          marginBottom: '1.5rem',
          color: '#757575',
        }}
      >
        Otherwise skip and finish network creation. You can come back to these settings anytime in
        the future.
      </Typography>
      <Panel paper>
        <RenderOptionalNetworkSettings
          Username={Username}
          canWrite={canWrite}
          Timezone={Timezone}
          addTracker={addTracker}
          publisherID={publisherID}
          saveAttempted={saveAttempted}
          uploadingSlateMedia={uploadingSlateMedia}
          NetworkTrackers={NetworkTrackers}
          handleDeleteError={handleDeleteError}
          handleDeleteSuccess={handleDeleteSuccess}
          handleTrackerEventTypeChange={handleTrackerEventTypeChange}
          handleDeleteModelWithPromise={handleDeleteModelWithPromise}
          handleTrackerEventPixelURLChange={handleTrackerEventPixelURLChange}
          handleTrackerEventActiveSwitchChange={handleTrackerEventActiveSwitchChange}
          addInVideoUnit={addInVideoUnit}
          NetworkInVideoUnits={NetworkInVideoUnits}
          handleInVideoUnitURLChange={handleInVideoUnitURLChange}
          handleInVideoUnitDeleteError={handleInVideoUnitDeleteError}
          handleInVideoUnitPartnerChange={handleInVideoUnitPartnerChange}
          handleInVideoUnitDeleteSuccess={handleInVideoUnitDeleteSuccess}
          handleInVideoUnitActiveSwitchChange={handleInVideoUnitActiveSwitchChange}
          handleInVideoUnitDeleteModelWithPromise={handleInVideoUnitDeleteModelWithPromise}
          SlateURL={SlateURL}
          updateSlateURL={updateSlateURL}
          uploadMediafile={uploadMediafile}
          handleTargetingChange={handleTargetingChange}
          TargetingSCTECueIDs={TargetingSCTECueIDs}
          TargetingSCTECueIDsIsAllowed={TargetingSCTECueIDsIsAllowed}
          NormalizeContentValues={NormalizeContentValues}
          handleToggleNormalizeContentValues={handleToggleNormalizeContentValues}
          AllowSilentAds={AllowSilentAds}
          handleToggleAllowSilentAds={handleToggleAllowSilentAds}
          BetaFeaturesAndHandlers={BetaFeaturesAndHandlers}
          handleSave={handleSave}
          onBack={onBack}
        />
      </Panel>
      <WizardButtons onNext={handleSave} onBack={onBack} nextButtonText='Finish & Save' />
    </>
  );
};

export const RenderOptionalNetworkSettings = ({
  canWrite,
  Username,
  Timezone,
  addTracker,
  publisherID,
  saveAttempted,
  uploadingSlateMedia,

  // Trackers
  NetworkTrackers,
  handleDeleteError,
  handleDeleteSuccess,
  handleTrackerEventTypeChange,
  handleDeleteModelWithPromise,
  handleTrackerEventPixelURLChange,
  handleTrackerEventActiveSwitchChange,

  // In Video Units
  addInVideoUnit,
  NetworkInVideoUnits,
  handleInVideoUnitURLChange,
  handleInVideoUnitDeleteError,
  handleInVideoUnitPartnerChange,
  handleInVideoUnitDeleteSuccess,
  handleInVideoUnitActiveSwitchChange,
  handleInVideoUnitDeleteModelWithPromise,

  // Slate
  SlateURL,
  updateSlateURL,
  uploadMediafile,

  // Targeting
  TargetingSCTECueIDs,
  handleTargetingChange,
  TargetingSCTECueIDsIsAllowed,

  // Content Value Normalization
  NormalizeContentValues,
  handleToggleNormalizeContentValues,

  // Allow Silent Ads
  AllowSilentAds,
  handleToggleAllowSilentAds,

  // Beta Features
  BetaFeaturesAndHandlers,
}: IOptionalNetworkSettingsFormProps) => {
  const [trackingVisible, setTrackingVisible] = useState(
    TargetingSCTECueIDs !== '' && TargetingSCTECueIDs !== undefined,
  );

  const trackerEvents = NetworkTrackers.map((trackerEvent: INetworkTracker) => {
    return {
      Active: trackerEvent.Active,
      ID: trackerEvent.ID,
      EventType: trackerEvent.EventType,
      PixelURL: trackerEvent.URL,
      Name: trackerEvent.ID.toString(),
    } as ITrackerEvent;
  });

  const videoUnits = NetworkInVideoUnits.map((unit: INetworkInVideoUnit) => {
    // using ITrackerEvent typing since we have the same basic struct
    return {
      Active: unit.Active,
      ID: unit.ID,
      EventType: unit.Partner,
      PixelURL: unit.URL,
      Name: unit.ID.toString(),
    } as ITrackerEvent;
  });

  const SCTECueIDTargetingRow = {
    renderType: 'TextfieldAndInclude',
    title1: 'SCTE Cue IDs Targeting',
    field1: 'TargetingSCTECueIDs',
    value1: TargetingSCTECueIDs,
    handleChange1: handleTargetingChange,
    isActiveValue1: () => TargetingSCTECueIDs && TargetingSCTECueIDs.length > 0,
    getActiveTag1: () => generateActiveTagForCommaSepField(TargetingSCTECueIDs, 'Custom Targeting'),
    placeholderText: `Enter SCTE Cue Splice Event ID Targeting here, can be comma separated.`,
    value2: TargetingSCTECueIDsIsAllowed !== EAllowStatus.DENY,
    handleChange2: () =>
      handleTargetingChange({
        target: {
          name: 'TargetingSCTECueIDsIsAllowed',
          value: reverseEAllowStatus(TargetingSCTECueIDsIsAllowed) as any,
        },
      } as any),
  } as IInputRow;

  const SCTECueIDTargetingPanel = {
    title: 'SCTE Cue Event ID',
    panelInactiveRowTag: 'None',
    panelActiveChipTitle: 'Custom',
    rows: [SCTECueIDTargetingRow],
  } as IAccordionPanel;

  const panels = [SCTECueIDTargetingPanel];

  return (
    <Rows largePadding>
      {/* Trackers */}
      <Row noPadding paddingBottom='16px'>
        <Text title>Trackers</Text>
        <Text muted extraSmall>
          Add tracking for any stream events
        </Text>
        {/* Events */}
        {trackerEvents && trackerEvents.length > 0 && (
          <TrackerEvents
            title={''}
            trackerEvents={trackerEvents}
            trackerEventTypes={NetworkTrackerEventTypes}
            handleTrackerEventActiveSwitchChange={handleTrackerEventActiveSwitchChange}
            handleTrackerEventTypeChange={handleTrackerEventTypeChange}
            handleTrackerEventPixelURLChange={handleTrackerEventPixelURLChange}
            handleDeleteModelWithPromise={handleDeleteModelWithPromise}
            handleDeleteSuccess={handleDeleteSuccess}
            handleDeleteError={handleDeleteError}
            displaySupportedWikiLink={false}
            saveAttempted={saveAttempted}
            publisherTimezone={Timezone}
          />
        )}
        <AddButton onClick={addTracker} />
      </Row>
      <Row noPadding paddingBottom='16px'>
        <Text title>In Video Units</Text>
        <Text muted extraSmall>
          Use innovative ad format
        </Text>
        {/* In Video Units */}
        {videoUnits && videoUnits.length > 0 && (
          <TrackerEvents
            title=''
            trackerEvents={videoUnits}
            trackerEventTypes={NetworkInVideoUnitPartners}
            handleTrackerEventActiveSwitchChange={handleInVideoUnitActiveSwitchChange}
            handleTrackerEventTypeChange={handleInVideoUnitPartnerChange}
            handleTrackerEventPixelURLChange={handleInVideoUnitURLChange}
            handleDeleteModelWithPromise={handleInVideoUnitDeleteModelWithPromise}
            handleDeleteSuccess={handleInVideoUnitDeleteSuccess}
            handleDeleteError={handleInVideoUnitDeleteError}
            displaySupportedWikiLink={false}
            saveAttempted={saveAttempted}
            enableInVideoUnit={true}
            publisherID={publisherID}
            username={Username}
            publisherTimezone={Timezone}
          />
        )}
        <AddButton onClick={addInVideoUnit} />
      </Row>
      {/* Slate */}
      <Row noPadding paddingBottom='16px'>
        <Text title>Slate</Text>
        <Text extraSmall muted>
          Upload slate to be inserted instead of ads
        </Text>
        <Button
          component='label'
          style={{
            marginTop: '5px',
            marginBottom: '20px',
          }}
        >
          <Typography
            style={{
              color: '#5c6bf8',
              fontSize: '16px',
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            + Add
          </Typography>
          <input
            type='file'
            style={{ display: 'none', width: '100%', height: '100%' }}
            disabled={uploadingSlateMedia}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              uploadMediafile(evt);
            }}
          />
        </Button>
        {!!SlateURL && (
          <>
            <a
              href={SlateURL}
              style={{
                color: 'black',
                marginLeft: '5px',
              }}
              target='_blank'
            >
              {SlateURL}
            </a>
            <Button
              onClick={() => {
                updateSlateURL('');
              }}
            >
              <DeleteIcon
                style={{
                  fill: '#707070',
                }}
              />
            </Button>
          </>
        )}
      </Row>
      <Row noPadding paddingBottom='16px'>
        {/* SCTE Targeting */}
        <Text title>SCTE Cue IDs Targeting</Text>
        <Text extraSmall muted>
          Checks the Splice Event ID of the SCTE Ad marker. Can be comma separated.
        </Text>
        {trackingVisible && (
          <>
            <div style={{ marginTop: '10px' }} />
            <MultiAccordion
              title='Targeting Parameters'
              panels={panels}
              disableEdit={canWrite === false}
            />
          </>
        )}
        {!trackingVisible && <AddButton onClick={() => setTrackingVisible(true)} />}
      </Row>
      <Row noPadding paddingBottom='16px'>
        <Cols style={{ alignItems: 'center' }}>
          <Col>
            <Text title>Content Value Normalization</Text>
          </Col>
          <Col>
            <Switch
              checked={NormalizeContentValues === EBool.EBool_TRUE}
              onChange={handleToggleNormalizeContentValues}
            />
          </Col>
        </Cols>
        <Text extraSmall muted>
          This feature will lower-case all characters, replace spaces with underscores, and remove
          special characters from all content values.
        </Text>
      </Row>
      <Row noPadding>
        <Cols style={{ alignItems: 'center' }}>
          <Col>
            <Text title>Allow Silent Ads</Text>
          </Col>
          <Col>
            <Switch
              checked={AllowSilentAds === EBool.EBool_TRUE}
              onChange={handleToggleAllowSilentAds}
            />
          </Col>
        </Cols>
        <Text extraSmall muted>
          This feature will allow silent ads or ads that have no audio channel.
        </Text>
      </Row>

      <Row noPadding paddingTop='16px'>
        {BetaFeaturesAndHandlers !== undefined && (
          <NetworkBetaFeatures
            betaChipRequired={BetaFeaturesAndHandlers.betaChipRequired}
            EnableTranscoderV3={BetaFeaturesAndHandlers.EnableTranscoderV3}
            toggleEnableTranscoderV3={BetaFeaturesAndHandlers.toggleEnableTranscoderV3}
            EnableAudioNormalization={BetaFeaturesAndHandlers.EnableAudioNormalization}
            toggleEnableAudioNormalization={BetaFeaturesAndHandlers.toggleEnableAudioNormalization}
            AudioNormalizeTarget={BetaFeaturesAndHandlers.AudioNormalizeTarget}
            setAudioNormalizationTarget={BetaFeaturesAndHandlers.setAudioNormalizationTarget}
            EnableLiveContext={BetaFeaturesAndHandlers.EnableLiveContext}
            handleEnableLiveContextChange={BetaFeaturesAndHandlers.handleEnableLiveContextChange}
            LiveContextLookbackMinutes={BetaFeaturesAndHandlers.LiveContextLookbackMinutes}
            EnablePadders={BetaFeaturesAndHandlers.EnablePadders}
            toggleEnablePadders={BetaFeaturesAndHandlers.toggleEnablePadders}
            uploadSSAIPadderAdMediafile={BetaFeaturesAndHandlers.uploadSSAIPadderAdMediafile}
            PadderAds={BetaFeaturesAndHandlers.PadderAds}
            setSSAIPadderAds={BetaFeaturesAndHandlers.setSSAIPadderAds}
            publisherID={BetaFeaturesAndHandlers.publisherID}
            isSuper={BetaFeaturesAndHandlers.isSuper}
            handleStreamFormInputChange={BetaFeaturesAndHandlers.handleStreamFormInputChange}
          />
        )}
      </Row>
    </Rows>
  );
};

// Generates the active tags for comma sep values for the accordions (i.e. 2 IDs, 4 Producers)
// fieldValue represents the textfield (contentid1,contentid2,contentid3) and
// fieldName represents the name to be shown (Content ID)
// multi-accordion-panel will ignore an empty string active tag
const generateActiveTagForCommaSepField = (fieldValue: string, fieldName: string) => {
  if (fieldValue && fieldValue.length > 0) {
    // split the field and get rid of empty strings to count
    const count = fieldValue.split(',').filter((part: string) => part !== '').length;
    return count === 1 ? `1 ${fieldName}` : `${count} ${fieldName}s`;
  } else {
    return '';
  }
};

interface IAddButton {
  onClick?: () => any;
  disabled?: boolean;
  children?: React.ReactNode;
}

const AddButton = ({ onClick, disabled, children }: IAddButton) => {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      style={{
        marginTop: '5px',
        marginBottom: '20px',
      }}
    >
      <Typography
        style={{
          color: '#5c6bf8',
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
        }}
      >
        + Add
        {children && <>{children}</>}
      </Typography>
    </Button>
  );
};

export default OptionalNetworkSettingsForm;
